import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { EmailContact } from '../../../../types/email';
import Pagination from '../../../common/Pagination';
import DataTable, { DataTableColumn, rowActionIconClass, rowActionDangerIconClass } from '../../../ui/DataTable';

interface ContactListProps {
  contacts: EmailContact[];
  onEdit: (contact: EmailContact) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
  searchLoading?: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  onPageChange: (page: number) => void;
}

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  onEdit,
  onDelete,
  loading,
  searchLoading,
  pagination,
  onPageChange,
}) => {
  const columns: DataTableColumn<EmailContact>[] = [
    { key: "firstName", header: "First Name", cell: (c) => <span className="font-medium">{c.firstName}</span> },
    { key: "lastName", header: "Last Name", cell: (c) => <span className="font-medium">{c.lastName}</span> },
    { key: "company", header: "Company", cell: (c) => c.company },
    { key: "email", header: "Email", cell: (c) => <span className="text-neutral-600">{c.email}</span> },
    { key: "role", header: "Role", cell: (c) => <span className="text-neutral-600">{c.role}</span> },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (contact) => (
        <div className="inline-flex gap-1">
          <div className="relative group">
            <button
              onClick={() => !contact.isGlobal && onEdit(contact)}
              disabled={contact.isGlobal}
              aria-label="Edit contact"
              className={rowActionIconClass}
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            {contact.isGlobal && (
              <div className="absolute right-0 z-10 hidden mb-1 bottom-full group-hover:block">
                <div className="px-2 py-1 text-xs text-white rounded whitespace-nowrap bg-neutral-800">
                  Global contacts cannot be edited
                </div>
              </div>
            )}
          </div>
          <div className="relative group">
            <button
              onClick={() => !contact.isGlobal && onDelete(contact.id)}
              disabled={contact.isGlobal}
              aria-label="Delete contact"
              className={rowActionDangerIconClass}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
            {contact.isGlobal && (
              <div className="absolute right-0 z-10 hidden mb-1 bottom-full group-hover:block">
                <div className="px-2 py-1 text-xs text-white rounded whitespace-nowrap bg-neutral-800">
                  Global contacts cannot be deleted
                </div>
              </div>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/*
        The spinner that used to sit above the table left stale rows visible
        underneath it while a search was in flight. DataTable's own loading
        state swaps those rows for skeletons instead, so what's on screen is
        never out of date with the search box.

        Both loads go through it: the first fetch used to short-circuit above
        with a headerless placeholder box, which is why this table alone lost
        its header row and column widths while loading.
      */}
      <DataTable
        columns={columns}
        rows={contacts ?? []}
        getRowKey={(contact) => contact.id}
        loading={loading || searchLoading}
        skeletonRows={6}
        emptyMessage="No contacts found. Add a contact or upload a CSV to get started."
      />

      {pagination.totalPages > 1 && (
        <div className="mt-3">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            setPage={onPageChange}
            totalItems={pagination.total}
            limit={pagination.limit}
          />
        </div>
      )}
    </div>
  );
};

export default ContactList;
